/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Verdi Dashboard API
 * API behind the Verdi irrigation dashboard: organizations, their devices, zones and irrigation schedules, and the sensor data those devices report.
 *
 * ## Authentication
 *
 * Most operations need a Descope session token, sent as `Authorization: Bearer <token>`. Each operation's `security` lists what it accepts. Role and permission names there, such as `Verdi Administrator`, are Descope roles or permissions the session must carry, and the operation's description repeats them under **Access**. Operations with `security: []` are public.
 *
 * ## Organization context
 *
 * Data belongs to organizations. A user's session acts as their primary organization, or as another organization they belong to when its ID is sent in the `User` header. Administrators must send the `User` header on organization-scoped operations, or the request is rejected with a 400.
 *
 * ## Errors
 *
 * Error bodies are JSON `{ "message": string }`.
 *
 * - **400**: the path, query or body failed validation, or the request broke a domain rule.
 * - **401 / 403**: sent by the auth middleware; the body may be empty.
 * - **409**: see below.
 * - **500**: an unexpected server error.
 *
 * ## Versioned resources
 *
 * Versioned resources carry a `versionId`. Send the `versionId` you last read on a PATCH (in the body) or a DELETE (as a query parameter). If the resource changed since, the request gets a 409: reload it and retry. A 409 that includes a `code` is a conflict specific to that operation, described on the operation. On a create, 409 means the resource already exists.
 *
 * ## Reads with a request body
 *
 * Some reads take their input as a JSON body on the HTTP `QUERY` method; these operations are marked `x-read-only: true`. Deprecated POST and GET variants serve clients that cannot send `QUERY`.
 *
 * ## Identifiers
 *
 * MongoDB ObjectIds are 24-character hexadecimal strings.
 * OpenAPI spec version: 1.0.0
 */

import type {
	DataTag,
	DefinedInitialDataOptions,
	DefinedUseQueryResult,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UndefinedInitialDataOptions,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "../custom-fetch.ts";
import type {
	ListLorawanDeviceKeys400,
	ListLorawanDeviceKeys500,
	ListLorawanDeviceKeysParams,
	LorawanDeviceKeys,
	LorawanDeviceKeysCreate,
	UpsertLorawanDeviceKeys400,
	UpsertLorawanDeviceKeys500,
} from "./schemas";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === "queryKey") continue;
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

export const getUpsertLorawanDeviceKeysUrl = () => {
	return `/lorawan-device-keys`;
};

/**
 * Only the latest keys are stored — an existing record with the same node_id is replaced. If `registered` is omitted it defaults to false.
 *
 * **Access:** Requires a session token; access keys are accepted. Requires the Production Jig Log R/W permission.
 * @summary Save the LORAWAN device keys submitted by the programming/test jig (upsert by node_id)
 */
export const upsertLorawanDeviceKeys = async (
	lorawanDeviceKeysCreate: LorawanDeviceKeysCreate,
	options?: Parameters<typeof customFetch>[1],
): Promise<LorawanDeviceKeys> => {
	const getHeaders = (
		h?: NonNullable<RequestInit["headers"]>,
	): Record<string, string | readonly string[]> => {
		if (!h) return {};
		if (h instanceof Headers) return Object.fromEntries(h.entries());
		if (Symbol.iterator in h) {
			return Object.fromEntries(
				Array.from(
					h as Iterable<Iterable<string>>,
					(entry) => Array.from(entry) as [string, string],
				),
			);
		}
		const headers: Record<string, string | readonly string[]> = {};
		for (const [name, value] of Object.entries<
			string | readonly string[] | undefined
		>(h)) {
			if (value !== undefined) headers[name] = value;
		}
		return headers;
	};
	return customFetch<LorawanDeviceKeys>(getUpsertLorawanDeviceKeysUrl(), {
		...options,
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			...getHeaders(options?.headers),
		},
		body: JSON.stringify(lorawanDeviceKeysCreate),
	});
};

export const getUpsertLorawanDeviceKeysMutationKey = () =>
	["upsertLorawanDeviceKeys"] as const;

export const getUpsertLorawanDeviceKeysMutationOptions = <
	TError = UpsertLorawanDeviceKeys400 | void | UpsertLorawanDeviceKeys500,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>,
		TError,
		UpsertLorawanDeviceKeysMutationVariables,
		TContext
	>;
	request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>,
	TError,
	UpsertLorawanDeviceKeysMutationVariables,
	TContext
> => {
	const mutationKey = getUpsertLorawanDeviceKeysMutationKey();
	const { mutation: mutationOptions, request: requestOptions } = options
		? options.mutation &&
			"mutationKey" in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey }, request: undefined };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>,
		UpsertLorawanDeviceKeysMutationVariables
	> = (props) => {
		const { data } = props ?? {};

		return upsertLorawanDeviceKeys(data, requestOptions);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpsertLorawanDeviceKeysMutationResult = NonNullable<
	Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>
>;
export type UpsertLorawanDeviceKeysMutationBody = LorawanDeviceKeysCreate;
export type UpsertLorawanDeviceKeysMutationError =
	| UpsertLorawanDeviceKeys400
	| void
	| UpsertLorawanDeviceKeys500;
export type UpsertLorawanDeviceKeysMutationVariables = {
	data: LorawanDeviceKeysCreate;
};

/**
 * @summary Save the LORAWAN device keys submitted by the programming/test jig (upsert by node_id)
 */
export const useUpsertLorawanDeviceKeys = <
	TError = UpsertLorawanDeviceKeys400 | void | UpsertLorawanDeviceKeys500,
	TContext = unknown,
>(
	options?: {
		mutation?: UseMutationOptions<
			Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>,
			TError,
			UpsertLorawanDeviceKeysMutationVariables,
			TContext
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseMutationResult<
	Awaited<ReturnType<typeof upsertLorawanDeviceKeys>>,
	TError,
	UpsertLorawanDeviceKeysMutationVariables,
	TContext
> => {
	return useMutation(
		getUpsertLorawanDeviceKeysMutationOptions(options),
		queryClient,
	);
};
export const getListLorawanDeviceKeysUrl = (
	params?: ListLorawanDeviceKeysParams,
) => {
	const normalizedParams = new URLSearchParams();

	Object.entries(params || {}).forEach(([key, value]) => {
		if (value !== undefined) {
			normalizedParams.append(
				key,
				value === null
					? "null"
					: value instanceof Date
						? value.toISOString()
						: String(value),
			);
		}
	});

	const stringifiedParams = normalizedParams.toString();

	return stringifiedParams.length > 0
		? `/lorawan-device-keys?${stringifiedParams}`
		: `/lorawan-device-keys`;
};

/**
 * **Access:** Requires a session token; access keys are accepted. Requires the Production Jig Log R/W permission.
 * @summary Fetch LORAWAN device keys based on date and registration status
 */
export const listLorawanDeviceKeys = async (
	params?: ListLorawanDeviceKeysParams,
	options?: Parameters<typeof customFetch>[1],
): Promise<LorawanDeviceKeys[]> => {
	return customFetch<LorawanDeviceKeys[]>(getListLorawanDeviceKeysUrl(params), {
		...options,
		method: "GET",
	});
};

export const getListLorawanDeviceKeysQueryKey = (
	params?: ListLorawanDeviceKeysParams,
) => {
	return ["lorawan-device-keys", ...(params ? [params] : [])] as const;
};

export const getListLorawanDeviceKeysQueryOptions = <
	TData = Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
	TError = ListLorawanDeviceKeys400 | void | ListLorawanDeviceKeys500,
>(
	params?: ListLorawanDeviceKeysParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
) => {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey =
		queryOptions?.queryKey ?? getListLorawanDeviceKeysQueryKey(params);

	const queryFn: QueryFunction<
		Awaited<ReturnType<typeof listLorawanDeviceKeys>>
	> = ({ signal }) =>
		listLorawanDeviceKeys(params, { signal, ...requestOptions });

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
		TError,
		TData
	> & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListLorawanDeviceKeysQueryResult = NonNullable<
	Awaited<ReturnType<typeof listLorawanDeviceKeys>>
>;
export type ListLorawanDeviceKeysQueryError =
	| ListLorawanDeviceKeys400
	| void
	| ListLorawanDeviceKeys500;

export function useListLorawanDeviceKeys<
	TData = Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
	TError = ListLorawanDeviceKeys400 | void | ListLorawanDeviceKeys500,
>(
	params: undefined | ListLorawanDeviceKeysParams,
	options: {
		query: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
				TError,
				TData
			>
		> &
			Pick<
				DefinedInitialDataOptions<
					Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
					TError,
					Awaited<ReturnType<typeof listLorawanDeviceKeys>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListLorawanDeviceKeys<
	TData = Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
	TError = ListLorawanDeviceKeys400 | void | ListLorawanDeviceKeys500,
>(
	params?: ListLorawanDeviceKeysParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
				TError,
				TData
			>
		> &
			Pick<
				UndefinedInitialDataOptions<
					Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
					TError,
					Awaited<ReturnType<typeof listLorawanDeviceKeys>>
				>,
				"initialData"
			>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListLorawanDeviceKeys<
	TData = Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
	TError = ListLorawanDeviceKeys400 | void | ListLorawanDeviceKeys500,
>(
	params?: ListLorawanDeviceKeysParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Fetch LORAWAN device keys based on date and registration status
 */

export function useListLorawanDeviceKeys<
	TData = Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
	TError = ListLorawanDeviceKeys400 | void | ListLorawanDeviceKeys500,
>(
	params?: ListLorawanDeviceKeysParams,
	options?: {
		query?: Partial<
			UseQueryOptions<
				Awaited<ReturnType<typeof listLorawanDeviceKeys>>,
				TError,
				TData
			>
		>;
		request?: SecondParameter<typeof customFetch>;
	},
	queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
	queryKey: DataTag<QueryKey, TData, TError>;
} {
	const queryOptions = getListLorawanDeviceKeysQueryOptions(params, options);

	const query = useQuery(queryOptions, queryClient) as UseQueryResult<
		TData,
		TError
	> & { queryKey: DataTag<QueryKey, TData, TError> };

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Fetch LORAWAN device keys based on date and registration status
 */
export const useSetListLorawanDeviceKeysQueryData = () => {
	const queryClient = useQueryClient();
	return (
		params: ListLorawanDeviceKeysParams | undefined,
		updater:
			| Awaited<ReturnType<typeof listLorawanDeviceKeys>>
			| undefined
			| ((
					old: Awaited<ReturnType<typeof listLorawanDeviceKeys>> | undefined,
			  ) => Awaited<ReturnType<typeof listLorawanDeviceKeys>> | undefined),
		$exactMatch: boolean = true,
	) => {
		queryClient.setQueriesData<
			Awaited<ReturnType<typeof listLorawanDeviceKeys>>
		>(
			{
				exact: $exactMatch,
				queryKey: getListLorawanDeviceKeysQueryKey(params),
			},
			updater,
		);
	};
};

/**
 * @summary Fetch LORAWAN device keys based on date and registration status
 */
export const useGetListLorawanDeviceKeysQueryData = () => {
	const queryClient = useQueryClient();
	return (params?: ListLorawanDeviceKeysParams) =>
		queryClient.getQueryData<Awaited<ReturnType<typeof listLorawanDeviceKeys>>>(
			getListLorawanDeviceKeysQueryKey(params),
		);
};
